import { call, put, takeEvery, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { authService, type AuthResponse } from '../../services/authService';
import { restaurantService } from '../../services/restaurantService';
import { setTokens, getTokens, clearTokens } from '../../api/tokenUtils';
import {
  loginRequest,
  loginSuccess,
  loginPending,
  loginFailure,
  refreshTokenRequest,
  fetchUserProfileRequest,
  forgotPasswordRequest,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPasswordRequest,
  resetPasswordSuccess,
  resetPasswordFailure,
  updatePasswordRequest,
  updatePasswordSuccess,
  updatePasswordFailure,
  verifyOtpRequest,
  verifyOtpSuccess,
  verifyOtpFailure,
  resendOtpRequest,
  resendOtpSuccess,
  resendOtpFailure,
  sendOtpRequest,
  sendOtpSuccess,
  sendOtpFailure,
  logout,
} from '../slices/authSlice';
import {
  fetchRestaurantsRequest,
  fetchRestaurantsSuccess,
  fetchRestaurantsFailure,
  clearRestaurants,
} from '../slices/restaurantSlice';

function* loginSaga(
  action: PayloadAction<{ email: string; password: string }>
): Generator<unknown, void, any> {
  try {
    const { email, password } = action.payload;
    
    const response: AuthResponse = yield call(authService.login, { email, password });

    const { user, token } = response;

    // Store user in localStorage (survives page refresh on OTP page)
    localStorage.setItem('pendingUser', JSON.stringify(user));
    localStorage.setItem('pendingToken', token);

    // Store user and token in pendingUser/pendingToken (NOT authenticated yet, waiting for OTP)
    // Do NOT store tokens in localStorage yet
    // Do NOT set isAuthenticated
    // Do NOT fetch restaurants yet
    yield put(loginPending({ user, token }));
    
    // Automatically send OTP after login success
    yield put(sendOtpRequest({ email: user.email }));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Login failed';
    yield put(loginFailure(errorMessage));
  }
}

function* refreshTokenSaga(): Generator<unknown, void, AuthResponse> {
  try {
    const response: AuthResponse = yield call(authService.refreshToken);
    const { user, token } = response;

    // Store new token in localStorage
    setTokens(token, token);

    // Update Redux state
    yield put(
      loginSuccess({
        user,
        accessToken: token,
        refreshToken: token,
      })
    );
  } catch {
    // Clear tokens and logout user
    clearTokens();
    yield put(logout());
  }
}

function* fetchUserProfileSaga(): Generator<unknown, void, unknown> {
  try {
    const userData = yield call(authService.getCurrentUser);
    const { accessToken, refreshToken } = getTokens();

    // Update Redux state with user profile
    yield put(
      loginSuccess({
        user: userData as any, // Type assertion for now since generator types are complex
        accessToken: accessToken || '',
        refreshToken: refreshToken || '',
      })
    );
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch user profile';
    yield put(loginFailure(errorMessage));
  }
}

function* forgotPasswordSaga(
  action: PayloadAction<{ email: string; type: string }>
): Generator<unknown, void, any> {
  try {
    const { email, type } = action.payload;
    
    const response: { status: boolean; message: string } = yield call(
      authService.forgotPassword,
      email,
      type
    );

    if (response.status) {
      yield put(forgotPasswordSuccess(response.message));
      // Note: OTP sending removed - component will handle it manually if needed
    } else {
      yield put(forgotPasswordFailure(response.message || 'Failed to send reset email'));
    }
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to send reset email';
    yield put(forgotPasswordFailure(errorMessage));
  }
}

function* resetPasswordSaga(
  action: PayloadAction<{ id: string; password: string }>
): Generator<unknown, void, any> {
  try {
    const { id, password } = action.payload;
    
    const response: { status?: boolean; message: string } = yield call(
      authService.resetPassword,
      id,
      password
    );

    // API returns {message: "Password Updated"} without status field
    // If we got here without error and have a message, it's a success
    if (response.message || response.status !== false) {
      yield put(resetPasswordSuccess(response.message));
    } else {
      yield put(resetPasswordFailure(response.message || 'Failed to reset password'));
    }
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to reset password';
    yield put(resetPasswordFailure(errorMessage));
  }
}

function* updatePasswordSaga(
  action: PayloadAction<{ id: string; password: string }>
): Generator<unknown, void, any> {
  try {
    const { id, password } = action.payload;
    
    const response: { status?: boolean; message: string } = yield call(
      authService.updatePassword,
      id,
      password
    );

    // API returns {message: "Password Updated"} without status field
    // If we got here without error and have a message, it's a success
    if (response.message || response.status !== false) {
      yield put(updatePasswordSuccess(response.message));
    } else {
      yield put(updatePasswordFailure(response.message || 'Failed to update password'));
    }
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to update password';
    yield put(updatePasswordFailure(errorMessage));
  }
}

function* verifyOtpSaga(
  action: PayloadAction<{ email: string; code: string }>
): Generator<unknown, void, any> {
  try {
    const { email, code } = action.payload;
    
    const response: { status?: boolean; message?: string; messsage?: string; data?: any; id?: string } = yield call(
      authService.verifyOtp,
      email,
      code
    );
    
    // API returns {messsage: "Successfully Verified", id: "..."} without status field
    // If we got here without error and have an ID, it's a success
    if (response.id || response.messsage || response.status !== false) {
      yield put(verifyOtpSuccess(response));
      
      // Get pendingUser and pendingToken from state
      const authState = yield select((state: any) => state.auth);
      const pendingUser = authState.pendingUser;
      const pendingToken = authState.pendingToken;
      
      if (pendingUser && pendingToken) {
        // Store tokens in localStorage
        setTokens(pendingToken, pendingToken);
        
        // Set OTP verified flag in localStorage
        localStorage.setItem('otpVerified', 'true');
        
        // Clear pendingUser and pendingToken from localStorage (cleanup after OTP success)
        localStorage.removeItem('pendingUser');
        localStorage.removeItem('pendingToken');
        
        // Move pendingUser to user and authenticate
        yield put(
          loginSuccess({
            user: pendingUser,
            accessToken: pendingToken,
            refreshToken: pendingToken,
          })
        );
        
        // Fetch restaurants after authentication
        yield put(fetchRestaurantsRequest());
      }
    } else {
      yield put(verifyOtpFailure(response.message || 'Failed to verify OTP'));
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Something went wrong';
    yield put(verifyOtpFailure(errorMessage));
  }
}

function* resendOtpSaga(
  action: PayloadAction<{ email: string }>
): Generator<unknown, void, any> {
  try {
    const { email } = action.payload;
    
    const response: { status: boolean; message: string } = yield call(
      authService.resendOtp,
      email
    );
    
    if (response.status) {
      yield put(resendOtpSuccess());
    } else {
      yield put(resendOtpFailure(response.message || 'Failed to resend OTP'));
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to resend OTP';
    yield put(resendOtpFailure(errorMessage));
  }
}

function* sendOtpSaga(
  action: PayloadAction<{ email: string }>
): Generator<unknown, void, any> {
  try {
    const { email } = action.payload;
    
    const response: { status?: boolean; message: string; email?: string } = yield call(
      authService.sendOtp,
      email
    );
    
    // API returns {message: "Mail Send", email: "..."} without status field
    // If we got here without error, it's a success
    if (response.message || response.status !== false) {
      yield put(sendOtpSuccess());
    } else {
      yield put(sendOtpFailure(response.message || 'Failed to send OTP'));
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to send OTP';
    yield put(sendOtpFailure(errorMessage));
  }
}

function* logoutSaga(): Generator<unknown, void, unknown> {
  try {
    // Clear ALL localStorage data (ensures complete cleanup)
    localStorage.clear();
    
    // Clear restaurants on logout
    yield put(clearRestaurants());
  } catch {
    // Handle error silently - no action needed for logout errors
  }
}

export function* watchLogin() {
  yield takeEvery(loginRequest.type, loginSaga);
}

export function* watchRefreshToken() {
  yield takeEvery(refreshTokenRequest.type, refreshTokenSaga);
}

export function* watchFetchUserProfile() {
  yield takeEvery(fetchUserProfileRequest.type, fetchUserProfileSaga);
}

export function* watchForgotPassword() {
  yield takeEvery(forgotPasswordRequest.type, forgotPasswordSaga);
}

export function* watchResetPassword() {
  yield takeEvery(resetPasswordRequest.type, resetPasswordSaga);
}

export function* watchUpdatePassword() {
  yield takeEvery(updatePasswordRequest.type, updatePasswordSaga);
}

function* fetchRestaurantsSaga(): Generator<unknown, void, any> {
  try {
    // Get the current API key and API base URL from the state
    const authState = yield select((state: any) => state.auth);
    const currentApiKey = authState.currentApiKey;
    const apiBaseUrl = authState.apiBaseUrl;
    
    if (!currentApiKey) {
      throw new Error('API key not available');
    }
    
    if (!apiBaseUrl) {
      throw new Error('API base URL not available');
    }
    
    const response = yield call(restaurantService.getRestaurants, apiBaseUrl, currentApiKey);
    yield put(fetchRestaurantsSuccess(response));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch restaurants';
    yield put(fetchRestaurantsFailure(errorMessage));
  }
}

export function* watchVerifyOtp() {
  yield takeEvery(verifyOtpRequest.type, verifyOtpSaga);
}

export function* watchResendOtp() {
  yield takeEvery(resendOtpRequest.type, resendOtpSaga);
}

export function* watchSendOtp() {
  yield takeEvery(sendOtpRequest.type, sendOtpSaga);
}

export function* watchLogout() {
  yield takeEvery(logout.type, logoutSaga);
}

export function* watchFetchRestaurants() {
  yield takeEvery(fetchRestaurantsRequest.type, fetchRestaurantsSaga);
}

// Export additional sagas for manual triggering if needed
export { refreshTokenSaga, fetchUserProfileSaga, fetchRestaurantsSaga };
